<template>
  <v-card
    v-if="visible"
    class="mb-4"
    variant="tonal"
    :color="bannerColor"
    data-test="language-job-progress"
  >
    <v-card-text class="py-3">
      <div class="d-flex align-center justify-space-between flex-wrap">
        <div class="d-flex align-center">
          <v-progress-circular
            v-if="jobStore.isRunning"
            indeterminate
            size="18"
            width="2"
            class="mr-3"
            data-test="language-job-spinner"
          />
          <v-icon v-else size="small" class="mr-3">{{ statusIcon }}</v-icon>
          <div v-if="job">
            <div class="text-body-2 font-weight-medium" data-test="language-job-status">
              {{ statusLabel }}
            </div>
            <div
              class="text-caption text-medium-emphasis"
              data-test="language-job-progress-count"
            >
              {{ progressLabel }}
            </div>
          </div>
        </div>

        <div v-if="isFinished" class="d-flex align-center" data-test="language-job-controls">
          <v-btn
            variant="text"
            size="small"
            data-test="language-job-toggle-details"
            @click="toggleDetails"
          >
            {{ showDetails ? 'Hide Details' : 'View Details' }}
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            data-test="language-job-dismiss"
            @click="jobStore.reset()"
          >
            Dismiss
          </v-btn>
        </div>
      </div>

      <div
        v-if="refreshWarning"
        class="text-caption mt-2"
        data-test="language-job-refresh-warning"
      >
        {{ refreshWarning }}
      </div>

      <template v-if="showDetails && isFinished && job">
        <div class="d-flex flex-wrap mt-3" data-test="language-job-summaries">
          <div class="mr-8 mb-2" data-test="description-counters">
            <div class="text-caption font-weight-medium text-uppercase" style="opacity: 0.7">
              Descriptions
            </div>
            <div class="text-caption">
              <span data-test="description-applied">
                Applied {{ job.counters.description.applied }}
              </span>
              <span
                v-if="job.counters.description.planned > 0"
                data-test="description-planned"
              >
                · Planned {{ job.counters.description.planned }}
              </span>
              <span data-test="description-skipped">
                · Skipped {{ job.counters.description.skipped }}
              </span>
              <span data-test="description-errors">
                · Errors {{ job.counters.description.errors }}
              </span>
            </div>
            <div class="text-caption" data-test="locked-description-skips">
              Locked descriptions skipped: {{ lockedSkips }}
            </div>
          </div>

          <div class="mr-8 mb-2" data-test="highlight-counters">
            <div class="text-caption font-weight-medium text-uppercase" style="opacity: 0.7">
              Highlights
            </div>
            <div class="text-caption">
              <span data-test="highlight-applied">
                Applied {{ job.counters.highlight.applied }}
              </span>
              <span
                v-if="job.counters.highlight.planned > 0"
                data-test="highlight-planned"
              >
                · Planned {{ job.counters.highlight.planned }}
              </span>
              <span data-test="highlight-skipped">
                · Skipped {{ job.counters.highlight.skipped }}
              </span>
              <span data-test="highlight-errors">
                · Errors {{ job.counters.highlight.errors }}
              </span>
            </div>
          </div>

          <div class="mb-2" data-test="job-totals">
            <div class="text-caption font-weight-medium text-uppercase" style="opacity: 0.7">
              Total
            </div>
            <div class="text-caption">
              <span data-test="total-applied">Applied {{ job.totals.applied }}</span>
              <span data-test="total-skipped"> · Skipped {{ job.totals.skipped }}</span>
              <span data-test="total-errors"> · Errors {{ job.totals.errors }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="isFailed"
          class="text-caption mt-2"
          data-test="failed-vin-details-unavailable"
        >
          Complete VIN-level details are unavailable because the job failed.
        </div>

        <template v-else-if="canLoadIssueResults">
          <div
            v-if="jobStore.resultDetailsState === 'loading'"
            class="text-caption mt-2"
            data-test="vin-details-loading"
          >
            Loading VIN details…
          </div>

          <div
            v-else-if="jobStore.resultDetailsState === 'failed'"
            class="d-flex align-center flex-wrap mt-2"
            data-test="vin-details-failure"
          >
            <span class="text-caption">VIN details could not be fully loaded.</span>
            <v-btn
              variant="text"
              size="small"
              data-test="vin-details-retry"
              @click="retryDetails"
            >
              Retry
            </v-btn>
          </div>

          <div
            v-else-if="jobStore.resultDetailsState === 'loaded'"
            class="vin-issue-table-scroll mt-3"
            data-test="vin-issue-table-scroll"
          >
            <v-table density="compact" data-test="vin-issue-table">
              <thead>
                <tr>
                  <th class="text-caption">VIN</th>
                  <th class="text-caption">Description</th>
                  <th class="text-caption">Highlight</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="result in jobStore.issueResults"
                  :key="result.vin"
                  :data-test="`vin-result-${result.vin}`"
                >
                  <td class="text-caption">{{ result.vin }}</td>
                  <td
                    class="text-caption text-wrap"
                    data-test="vin-description-outcome"
                    :data-status="result.description.status"
                  >
                    <template v-if="isIssueOutcome(result.description)">
                      {{ describeContentOutcomeReason(result.description) }}
                    </template>
                    <template v-else>
                      <span class="text-medium-emphasis">
                        {{ describeOperation(result.description.operation) }} ·
                        {{ describeOutcome(result.description.status) }}
                      </span>
                    </template>
                  </td>
                  <td
                    class="text-caption text-wrap"
                    data-test="vin-highlight-outcome"
                    :data-status="result.highlight.status"
                  >
                    <template v-if="isIssueOutcome(result.highlight)">
                      {{ describeContentOutcomeReason(result.highlight) }}
                    </template>
                    <template v-else>
                      <span class="text-medium-emphasis">
                        {{ describeOperation(result.highlight.operation) }} ·
                        {{ describeOutcome(result.highlight.status) }}
                      </span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </template>
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import {
  describeContentOutcomeReason,
  describeOperation,
  describeOutcome,
  describeRefreshFailure,
  languageJobBannerColor,
  languageJobProgressLabel,
  languageJobStatusLabel,
  lockedDescriptionSkips,
  type ContentOutcome,
} from '../languageJob'
import { useLanguageJobStore } from '../stores/languageJob'

const jobStore = useLanguageJobStore()
const showDetails = ref(false)

const job = computed(() => jobStore.job)
const visible = computed(() => jobStore.hasJob)
const isFailed = computed(() => jobStore.status === 'failed')
const isCompleted = computed(() => jobStore.status === 'completed')
const isFinished = computed(() => isFailed.value || isCompleted.value)

const statusLabel = computed(() => (job.value ? languageJobStatusLabel(job.value) : ''))
const progressLabel = computed(() => (job.value ? languageJobProgressLabel(job.value) : ''))
const bannerColor = computed(() => (job.value ? languageJobBannerColor(job.value) : 'primary'))
const statusIcon = computed(() =>
  isFailed.value ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline',
)

// A refresh failure remains distinct from the durable job status.
const refreshWarning = computed(() => describeRefreshFailure(jobStore.refreshFailure))
const lockedSkips = computed(() => lockedDescriptionSkips(job.value?.counters))
const canLoadIssueResults = computed(
  () =>
    showDetails.value &&
    job.value?.status === 'completed' &&
    job.value.mode === 'apply' &&
    job.value.totals.skipped + job.value.totals.errors > 0,
)

const isIssueOutcome = (outcome: ContentOutcome): boolean =>
  outcome.status === 'skipped' || outcome.status === 'error'

const toggleDetails = () => {
  if (!isFinished.value) return
  showDetails.value = !showDetails.value
  if (canLoadIssueResults.value) void jobStore.loadIssueResults()
}

const retryDetails = () => {
  void jobStore.retryIssueResults()
}

watch(
  () => jobStore.jobId,
  () => {
    showDetails.value = false
  },
)

// Remounting rejoins the job the store is already tracking. `startPolling` is
// a no-op when a poller is running, so this cannot produce a second one.
onMounted(() => {
  if (jobStore.hasJob) jobStore.startPolling()
})
</script>

<style scoped>
.vin-issue-table-scroll {
  max-height: 360px;
  overflow-y: auto;
  overflow-x: auto;
}

/* Let the outer scrollport own scrolling so sticky headers work. */
.vin-issue-table-scroll :deep(.v-table__wrapper) {
  overflow: visible;
}

.vin-issue-table-scroll :deep(thead th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}

.vin-issue-table-scroll :deep(td.text-wrap) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
